import {GridSortModel} from '@mui/x-data-grid';
import _ from 'lodash';
import {ReviewResultsSources} from '../../../../../shared/interfaces';
import {LeftPanelDataResponse} from '../../../../../api/types/reportingApi.type';
import {
  AccordionSection,
  FilterComponent,
  Reports,
  Section,
} from '../../config/reportTypes';
import {buildTreeItems} from '../../../../../utils/reportUtils';
import {
  LeftPanelReportResponse,
  SaveAssignGroupGridReportDataReqPayload,
} from '../../../../../api/types/reportingApi.type';
import {getSectionMeta} from '../../component/left-panel/components/ArrSourceSelectedSection';

type TreeItem = {
  id: string;
  fileType: string;
  children: TreeItem[];
};

export const updateApplyButtonState = (
  report: Reports,
  filterValues: Record<string, any>
) => {
  // Assignment Level (reviewers) is optional - not required for Apply button
  const isReviewerSelected: boolean =
    Array.isArray(filterValues.selectedReviewers) &&
    filterValues.selectedReviewers.some((v) => v === true);

  // Assignment Group is required
  const isAssignmentsSelected: boolean =
    Array.isArray(filterValues.selectedAssignments) &&
    filterValues.selectedAssignments.length > 0;

  // Only assignments are required (matching Batch Management pattern but without sources)
  const appplyButtonDisabled = !isAssignmentsSelected;

  const config = report.config;
  const configObject = {
    ...config,
    selectionCriteria: {
      ...config.selectionCriteria,
      actions: config.selectionCriteria.actions.map((action) => {
        if (
          action.input_type === 'button' &&
          action.actionKey === 'applyChanges'
        ) {
          return {
            ...action,
            disabled: appplyButtonDisabled,
          };
        } else if (
          action.input_type === 'button' &&
          action.actionKey === 'reset'
        ) {
          // Reset button enabled if any filter is selected (matching Batch Management pattern)
          return {
            ...action,
            disabled: !isReviewerSelected && !isAssignmentsSelected,
          };
        } else {
          return action;
        }
      }),
    },
  };
  return {
    ...report,
    config: configObject,
  };
};

const getFolderIds = (arr: TreeItem[]): string[] => {
  let folderIds: string[] = [];

  arr.forEach((item) => {
    if (item.fileType === 'folder') {
      folderIds.push(item.id);
      if (item.children && item.children.length > 0) {
        folderIds = folderIds.concat(getFolderIds(item.children));
      }
    }
  });

  return folderIds;
};

export const transformLeftPanelDataToReviewResultsSources = (
  leftPanelData: LeftPanelDataResponse
): ReviewResultsSources => {
  return {
    securityGroupList: [],
    reviewersList: leftPanelData.assignmentClassificationList.map((item) => ({
      keyID: item.classificationID,
      kayName: item.tabName,
    })),
    custodiansList: [],
    searchesList: [],
    assignmentsList: leftPanelData.lstAssignmentGroup.map((item) => ({
      id: item.id,
      text: item.text,
      parent: item.parent,
      isGroup: item.isGroup,
      type: item.type ?? '',
    })),
    foldersList: [],
    documentTypesList: [],
    actionTypesList: [],
    tagsList: [],
    redactionTagsList: [],
    commentsTypeList: [],
    productionsList: [],
    tagClassifications: [],
  };
};

export const mergeApiDataIntoConfig = (
  report: Reports,
  apiData: ReviewResultsSources,
  savedReportData?: LeftPanelReportResponse
) => {
  const config = report.config;
  const configObject = {
    sections: config.sections.map((section: Section) => ({
      ...section,
      filters: section.filters.map((filter: AccordionSection) => {
        return {
          ...filter,
          components: filter.components.map((comp: FilterComponent) => {
            if (
              comp.input_type === 'checkboxList' &&
              filter.id === 'reviewersList'
            ) {
              const transformedData = apiData.reviewersList.map((reviewer) => ({
                label: reviewer.kayName,
                value: (
                  savedReportData?.selectedAssignmentClassification ?? []
                ).includes(reviewer.keyID),
              }));
              return {
                ...comp,
                childLabels: transformedData,
              };
            } else if (
              comp.input_type === 'multiSelectTreeViewList' &&
              filter.id === 'assignments'
            ) {
              const transformedData = buildTreeItems(apiData.assignmentsList);
              return {
                ...comp,
                treeData: transformedData,
                defaultExpandedIds: getFolderIds(transformedData),
                initialValues: (savedReportData?.selectedAssignments ?? []).map(
                  (id) => id.toString()
                ),
              };
            } else if (
              comp.input_type === 'checkbox' &&
              filter.id === 'includeAssignmentDetail'
            ) {
              return {
                ...comp,
                checked: savedReportData?.include_Assignment_Detail === 1,
              };
            }
            return comp;
          }),
        };
      }),
    })),
  };

  // Handle selectionCriteria.fields (for the toggle)
  const selectionCriteriaFields = config.selectionCriteria?.fields?.map(
    (field: any) => {
      if (
        field.input_type === 'toggle' &&
        field.key === 'includeAssignmentDetail'
      ) {
        // Always default to true (on) - don't use saved report data
        return {
          ...field,
          value: true,
        };
      }
      return field;
    }
  );

  return {
    ...report,
    config: {
      selectionCriteria: {
        ...config.selectionCriteria,
        fields: selectionCriteriaFields ?? config.selectionCriteria?.fields,
      },
      sections: configObject.sections,
    },
  };
};

export const getSaveAssignGroupGridReportDataPayload = (
  reportConfig: Reports,
  filterValues: Record<string, any>,
  apiData?: ReviewResultsSources,
  pageNumber: number = 1,
  pageSize: number = 50,
  selectedCustomColumns: number[] = []
) => {
  const payload: SaveAssignGroupGridReportDataReqPayload = {
    selectedAssignments: [],
    selectedAssignmentClassification: [],
    include_Assignment_Detail: 1,
    fromDateTime: _.get(filterValues, 'fromDateTime', ''),
    toDateTime: _.get(filterValues, 'toDateTime', ''),
    pageNumber: pageNumber,
    pageSize: pageSize,
    assignmentSearchKey: _.get(filterValues, 'assignmentSearchKey', ''),
    reportTypeCode: 'APR001',
    documentsNotinAnyAssignment: _.get(
      filterValues,
      'documentsNotinAnyAssignment',
      false
    ),
    selectedCustomColumns: selectedCustomColumns,
  };

  // Iterate through the report config to map filter values to payload based on section filter component
  reportConfig.config.sections.forEach((section: Section) => {
    section.filters.forEach((filter: AccordionSection) => {
      filter.components.forEach((comp: FilterComponent) => {
        const key = comp.key;
        if (key === 'selectedAssignments') {
          // selectedAssignments is already an array of strings
          payload.selectedAssignments = _.get(filterValues, key, []);
        } else if (key === 'selectedReviewers') {
          // Convert boolean array to classification ID strings
          const selectedClassification: string[] = [];
          _.get(filterValues, key, []).forEach(
            (isSelected: boolean, index: number) => {
              if (isSelected && apiData) {
                const classificationId = _.get(
                  apiData.reviewersList[index],
                  'keyID',
                  0
                );
                selectedClassification.push(classificationId.toString());
              }
            }
          );
          payload.selectedAssignmentClassification = selectedClassification;
        }
      });
    });
  });

  return payload;
};

export const getReviewProgressAssignmentReportXmlPayload = (
  reportConfig: Reports,
  filterValues: Record<string, any>,
  apiData?: ReviewResultsSources,
  reportId: number = 0,
  pageNumber: number = 1,
  pageSize: number = 50
) => {
  const selectedAssignments: string[] = [];
  const selectedAssignmentClassification: string[] = [];

  // Iterate through the report config to map filter values to payload
  reportConfig.config.sections.forEach((section: Section) => {
    section.filters.forEach((filter: AccordionSection) => {
      filter.components.forEach((comp: FilterComponent) => {
        const key = comp.key;
        if (key === 'selectedAssignments') {
          selectedAssignments.push(..._.get(filterValues, key, []));
        } else if (key === 'selectedReviewers') {
          // Convert boolean array to classification ID strings
          _.get(filterValues, key, []).forEach(
            (isSelected: boolean, index: number) => {
              if (isSelected && apiData) {
                const classificationId = _.get(
                  apiData.reviewersList[index],
                  'keyID',
                  0
                ).toString();
                selectedAssignmentClassification.push(classificationId);
              }
            }
          );
        }
      });
    });
  });

  return {
    reportId: reportId,
    reportTypeCode: 'APR001',
    selectedAssignments: selectedAssignments,
    selectedAssignmentClassification: selectedAssignmentClassification,
    fromDateTime: _.get(filterValues, 'fromDateTime', ''),
    toDateTime: _.get(filterValues, 'toDateTime', ''),
    include_Assignment_Detail: 1,
    pageNumber: pageNumber,
    pageSize: pageSize,
    documentsNotinAnyAssignment: _.get(
      filterValues,
      'documentsNotinAnyAssignment',
      false
    ),
    selectedCustomColumns: _.get(filterValues, 'selectedCustomColumns', []),
    isSavedSearchSource: false,
  };
};

export const getDefaultSorting = (): GridSortModel => {
  return [
    {
      field: 'assignment',
      sort: 'asc',
    },
  ];
};

export const getAppliedFilterValueObject = (
  apiData: ReviewResultsSources,
  savedReportData?: LeftPanelReportResponse
) => {
  const filterObject: {[key: string]: any} = {};

  if (!savedReportData) return filterObject;

  filterObject.selectedAssignments = (
    savedReportData?.selectedAssignments ?? []
  ).map((id) => id.toString());

  filterObject.selectedReviewers = apiData.reviewersList.map((reviewer) =>
    (savedReportData?.selectedAssignmentClassification ?? []).includes(
      reviewer.keyID
    )
  );

  // Assignment Report doesn't use sources, so we don't need to handle selectedSource
  // Only include source-related data if selectedSource exists and is not null
  if (savedReportData?.selectedSource) {
    const sourceMeta = getSectionMeta(
      savedReportData.selectedSource.toLowerCase() ?? ''
    );

    filterObject[savedReportData.selectedSource] = [
      {
        sourceName: sourceMeta.sourceName,
        navigationSourceType: sourceMeta.navigationSourceType,
        source: (savedReportData.selectedSourceID ?? []).map((id) =>
          id.toString()
        ),
      },
    ];
  }

  return filterObject;
};

export const getReviewProgressAssignmentExportPayload = (
  reportConfig: Reports,
  filterValues: Record<string, any>,
  apiData?: ReviewResultsSources,
  pageNumber: number = 1,
  pageSize: number = 50,
  selectedCustomColumns: number[] = []
) => {
  const selectionCriteria = {
    selectedAssignments: [] as string[],
    selectedAssignmentClassification: [] as string[],
    documentsNotinAnyAssignment: _.get(
      filterValues,
      'documentsNotinAnyAssignment',
      false
    ),
    FromDateTime: _.get(filterValues, 'fromDateTime', ''),
    ToDateTime: _.get(filterValues, 'toDateTime', ''),
    include_Assignment_Detail: 1,
    pageNumber: pageNumber,
    pageSize: pageSize,
    selectedCustomColumns: selectedCustomColumns,
    dataAsOf: new Date().toISOString(),
  };

  // Iterate through the report config to map filter values to payload
  reportConfig.config.sections.forEach((section: Section) => {
    section.filters.forEach((filter: AccordionSection) => {
      filter.components.forEach((comp: FilterComponent) => {
        const key = comp.key;
        if (key === 'selectedAssignments') {
          selectionCriteria.selectedAssignments = _.get(filterValues, key, []);
        } else if (key === 'selectedReviewers') {
          // Convert boolean array to classification ID strings
          const selectedClassification: string[] = [];
          _.get(filterValues, key, []).forEach(
            (isSelected: boolean, index: number) => {
              if (isSelected && apiData) {
                const classificationId = _.get(
                  apiData.reviewersList[index],
                  'keyID',
                  0
                );
                selectedClassification.push(classificationId.toString());
              }
            }
          );
          selectionCriteria.selectedAssignmentClassification =
            selectedClassification;
        }
      });
    });
  });

  return {
    selectionCriteria,
    actionType: 'EXPORTREPORT',
    reportType: 'APR001',
  };
};
